import { useCallback, useEffect, useRef, useState } from 'react';
import { compact, merge, isEmpty } from 'lodash-es';
import { useBatchedChunks } from 'site-modules/shared/hooks/use-batched-chunks';
import { useServerEvents } from 'site-modules/shared/hooks/use-server-events';
import { useMarkdownStream } from 'site-modules/canvas/hooks/use-markdown-stream';
import { useTimeout } from 'site-modules/shared/hooks/use-timeout';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { RESPONSE_ERROR } from 'site-modules/canvas/constants/canvas';

const RESPONSE_TIMEOUT = 45000;

export const checkStreamCompleteness = data => ['stop', 'finish'].includes(data.finish);

export function useResponseStream(id, { bodyStream, onComplete, onError, isCompleteOnly, creativeId }) {
  const [response, setResponse] = useState('');
  const responseRef = useRef('');
  const [structData, setStructData] = useState(null);
  const [isTimeout, setIsTimeout] = useState(false);
  const [setResponseTimeout, clearResponseTimeout] = useTimeout();
  const hasResponse = useRef(false);
  const isTimedOutRef = useRef(false);

  const trackError = useCallback(
    trackingData => {
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
        event_data: {
          action_name: TrackingConstant.ACTION_SHOW_CONTENT,
          action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
          action_cause: TrackingConstant.ACTION_CAUSE_USER_INPUT,
          creative_id: creativeId,
          ...(id ? { resp_uuid: id } : {}),
          ...trackingData,
        },
      });
    },
    [creativeId, id]
  );

  const updateTimeout = useCallback(timeout => {
    isTimedOutRef.current = timeout;
    setIsTimeout(timeout);
  }, []);

  useEffect(() => {
    if (id || bodyStream) {
      // Reset response state when a new external stream starts.
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional stream-session reset
      setResponse('');
      responseRef.current = '';
      setStructData(null);
      updateTimeout(false);
      clearResponseTimeout();
      setResponseTimeout(() => {
        updateTimeout(true);
      }, RESPONSE_TIMEOUT);
      hasResponse.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, bodyStream]);

  const handleChunkFlash = useCallback(
    chunks => {
      if (!isCompleteOnly) {
        setResponse(prevResponse => compact([prevResponse, ...chunks]).join(''));
      }
    },
    [isCompleteOnly]
  );

  const pushChunk = useBatchedChunks(handleChunkFlash, 16);

  const handleData = useCallback(
    (eventName, data) => {
      if (isTimedOutRef.current) {
        return;
      }

      clearResponseTimeout();
      hasResponse.current = hasResponse.current || !!data.delta?.length;
      responseRef.current = compact([responseRef.current, ...(data.delta || [])]).join('');
      pushChunk(data.delta);

      if (!isEmpty(data.struct)) {
        setStructData(prev => merge({}, prev, data.struct));
      }
    },
    [clearResponseTimeout, pushChunk]
  );

  const handleError = useCallback(
    readyState => {
      if (readyState === EventSource.CLOSED) {
        clearResponseTimeout();
        trackError({
          subaction_name: 'message_content_error',
          value: 'closed',
        });
        onError?.(RESPONSE_ERROR.CANT_ASSIST);
      }
    },
    [clearResponseTimeout, onError, trackError]
  );

  const handleComplete = useCallback(() => {
    if (!hasResponse.current) {
      trackError({
        subaction_name: 'message_content_error',
        value: 'empty',
      });
      onError?.(RESPONSE_ERROR.CANT_ASSIST);
    } else {
      onComplete(responseRef.current);
    }
  }, [onComplete, onError, trackError]);

  // Only one of useServerEvents or useMarkdownStream should work (id or bodyStream)
  const stopResponse = useServerEvents({
    onData: handleData,
    onError: handleError,
    onComplete: handleComplete,
    url: id ? `/llm/v1/sse/${id}/?meta=True&profile=False&input=True` : null,
    eventNames: ['delta'],
    checkStreamCompleteness,
  });

  useEffect(() => {
    if (isTimeout) {
      stopResponse();
      trackError({
        subaction_name: 'timeout_error',
      });

      onError?.(RESPONSE_ERROR.SOMETHING_WRONG);
    }
  }, [isTimeout, onError, stopResponse, trackError]);

  useMarkdownStream(bodyStream, { onData: handleData, onComplete: handleComplete });

  return [response, stopResponse, structData];
}
